// Signage → Anunciantes (/signage/anunciantes). Marcas que pautan en las pantallas.
// Alta simple; el vínculo a partner (para facturar) es opcional y va en Contratos.
// Dashboard por anunciante (script 416): inversión, emisión real, audiencia en
// franja, CPM efectivo, SOV real vs pactado, cupones atribuidos y ventas de SUS
// SKUs (vía marca) vs el período previo — el reporte de rendición para la marca.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Building2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigRpc, sigUpdate } from './shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface Anunciante { id: string; nombre: string; partner_id: string | null; marca_id: string | null; contacto: string | null; email: string | null; telefono: string | null; activo: boolean }

interface Dash {
  anunciante: string; marca: string | null; desde: string; hasta: string;
  resumen: { inversion: number; contratos: number; impresiones: number; segundos: number; audiencia: number; cpm: number | null; certificados: number };
  contratos: { nombre: string; estado: string; fecha_inicio: string; fecha_fin: string; monto: number; facturado: boolean; pantallas: number; impresiones: number; audiencia: number }[];
  anuncios: { nombre: string; estado: string; impresiones: number; sov_pactado_pct: number; sov_real_pct: number | null }[];
  cupones: { canjes: number; descuento: number; ventas: number };
  marca_ventas: { unidades: number; monto: number; monto_prev: number; delta_pct: number | null; top: { nombre: string; unidades: number; monto: number }[] } | null;
}
const money = (n: number) => `$${Number(n ?? 0).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSeg = (s: number) => { if (s < 60) return `${s}s`; const m = Math.round(s / 60); if (m < 60) return `${m} min`; const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; };

function DashboardAnunciante({ id, onClose }: { id: string; onClose: () => void }) {
  const [dias, setDias] = useState(90);
  const { data: d, isLoading } = useQuery<Dash>({
    queryKey: ['sig-an-dash', id, dias], staleTime: 0,
    queryFn: () => sigRpc<Dash>('sig_anunciante_dashboard', { _id: id, _dias: dias }),
  });
  return (
    <Modal title={`Dashboard — ${d?.anunciante ?? '…'}`} onClose={onClose}>
      <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{d ? `${d.desde} → ${d.hasta}` : ''}{d?.marca ? ` · marca: ${d.marca}` : ''}</span>
          <div className="ml-auto inline-flex rounded-full border overflow-hidden text-xs">
            {[30, 90, 180].map((n) => (
              <button key={n} className={`px-2.5 py-1 ${dias === n ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`} onClick={() => setDias(n)}>{n} días</button>
            ))}
          </div>
        </div>
        {isLoading && <div className="py-8 text-center text-muted-foreground">Calculando…</div>}
        {d && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {[
                { l: 'Inversión', v: money(d.resumen.inversion) },
                { l: 'Reproducciones', v: Number(d.resumen.impresiones).toLocaleString('es') },
                { l: 'Tiempo emitido', v: fmtSeg(Number(d.resumen.segundos)) },
                { l: 'Audiencia en franja', v: `${Number(d.resumen.audiencia).toLocaleString('es')} tickets` },
                { l: 'CPM efectivo', v: d.resumen.cpm != null ? money(d.resumen.cpm) : '—' },
                { l: 'Certificados', v: String(d.resumen.certificados) },
              ].map((k) => (
                <div key={k.l} className="rounded-md border p-2 text-center">
                  <div className="font-bold tabular-nums">{k.v}</div>
                  <div className="text-[11px] text-muted-foreground">{k.l}</div>
                </div>
              ))}
            </div>

            {d.contratos.length > 0 && (
              <div>
                <div className="font-medium mb-1">Contratos de franja</div>
                {d.contratos.map((c, i) => (
                  <div key={i} className="flex items-baseline gap-2 border-b last:border-0 py-1 text-xs">
                    <span className="font-medium">{c.nombre}</span>
                    <span className="text-muted-foreground">{c.fecha_inicio}→{c.fecha_fin} · {c.pantallas} pant. · {Number(c.impresiones).toLocaleString('es')} repro · {Number(c.audiencia).toLocaleString('es')} tickets</span>
                    <span className="ml-auto tabular-nums font-medium">{money(c.monto)}</span>
                    <span className={c.facturado ? 'text-emerald-600' : 'text-amber-600'}>{c.facturado ? '✓ fact.' : 'sin facturar'}</span>
                  </div>
                ))}
              </div>
            )}

            {d.anuncios.length > 0 && (
              <div>
                <div className="font-medium mb-1">Anuncios (retail media) — SOV real vs pactado</div>
                {d.anuncios.map((a, i) => (
                  <div key={i} className="py-1 text-xs space-y-0.5">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium">{a.nombre}</span><span className="text-muted-foreground">({a.estado})</span>
                      <span className="ml-auto tabular-nums">{Number(a.impresiones).toLocaleString('es')} repro · real {a.sov_real_pct != null ? `${a.sov_real_pct}%` : '—'} / pactado {a.sov_pactado_pct}%</span>
                    </div>
                    <div className="relative h-2 rounded bg-muted overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-primary/70" style={{ width: `${Math.min(100, a.sov_real_pct ?? 0)}%` }} />
                      <div className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: `${Math.min(100, a.sov_pactado_pct)}%` }} title={`pactado ${a.sov_pactado_pct}%`} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {d.cupones.canjes > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-900/10 p-2 text-xs">
                🎟 <b>{d.cupones.canjes}</b> canje(s) de cupones atribuidos a sus pautas · descuento {money(d.cupones.descuento)} · <b>{money(d.cupones.ventas)}</b> en ventas con atribución directa pantalla→caja.
              </div>
            )}

            {d.marca_ventas && (
              <div>
                <div className="font-medium mb-1">Ventas de sus SKUs (marca {d.marca})</div>
                <div className="text-xs text-muted-foreground mb-1">
                  {Number(d.marca_ventas.unidades).toLocaleString('es')} uds · {money(d.marca_ventas.monto)} en el período
                  {' '}vs {money(d.marca_ventas.monto_prev)} el período previo
                  {d.marca_ventas.delta_pct != null && (
                    <b className={d.marca_ventas.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}> ({d.marca_ventas.delta_pct >= 0 ? '+' : ''}{d.marca_ventas.delta_pct}%)</b>
                  )}
                </div>
                {d.marca_ventas.top.map((t, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5 border-b last:border-0">
                    <span className="truncate mr-2">{t.nombre}</span>
                    <span className="tabular-nums text-muted-foreground">{Number(t.unidades).toLocaleString('es')} uds · {money(t.monto)}</span>
                  </div>
                ))}
                {!d.marca_ventas.top.length && <div className="text-xs text-muted-foreground">Sin ventas de la marca en el período.</div>}
              </div>
            )}
            {!d.marca_ventas && <p className="text-xs text-muted-foreground">Vincula la <b>marca</b> del anunciante (editar) para medir las ventas de sus SKUs.</p>}
            <p className="text-[11px] text-muted-foreground border-t pt-2">
              Audiencia = tickets de caja dentro de las franjas contratadas (auditable en el POS). SOV real medido con el proof-of-play.
              El comparativo de la marca es antes/después del mismo largo, no aísla otros factores.
            </p>
          </>
        )}
        <div className="flex justify-end"><Button variant="ghost" onClick={onClose}>Cerrar</Button></div>
      </div>
    </Modal>
  );
}

export function Anunciantes() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Anunciante> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { data: rows = [] } = useQuery<Anunciante[]>({ queryKey: ['sig-anunciantes'], queryFn: () => sigList<Anunciante>('anunciante?select=*&deleted_at=is.null&order=nombre') });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [] });
  const clientes = partners.filter((p) => (p.tipo_partner || []).includes('cliente'));
  const { data: marcas = [] } = useQuery<{ id: string; nombre: string }[]>({
    queryKey: ['sig-marcas'],
    queryFn: async () => (await apiClient.get<{ id: string; nombre: string }[]>('/rest/inventario.marca?select=id,nombre&deleted_at=is.null&order=nombre')).data,
    staleTime: 5 * 60 * 1000,
  });
  const [dashId, setDashId] = useState<string | null>(null);
  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-anunciantes'] });

  const guardar = async () => {
    if (!edit?.nombre) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      const body = { nombre: edit.nombre, partner_id: edit.partner_id || null, marca_id: edit.marca_id || null, contacto: edit.contacto || null, email: edit.email || null, telefono: edit.telefono || null, activo: edit.activo ?? true };
      if (edit.id) await sigUpdate('anunciante', edit.id, body); else await sigInsert('anunciante', body);
      setEdit(null); invalidar(); toast.success('Anunciante guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };
  const eliminar = (r: Anunciante) => sigUpdate('anunciante', r.id, { deleted_at: new Date().toISOString(), activo: false }).then(invalidar).then(() => toast.success('Eliminado')).catch((e) => toast.error(getErrorMessage(e)));

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Building2 className="w-5 h-5" /> Anunciantes</h1>
        <Button className="ml-auto" onClick={() => setEdit({ activo: true })}><Plus className="w-4 h-4 mr-1" /> Nuevo anunciante</Button>
      </div>
      <p className="text-sm text-muted-foreground">Marcas/laboratorios que pautan en tus pantallas. Luego les vendes <strong>franjas</strong> en Contratos de franja.</p>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Anunciante</th>
            <th className="py-2 px-3 text-left">Contacto</th><th className="py-2 px-3 text-left">Email</th>
            <th className="py-2 px-3 text-center">Activo</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3 font-medium">{r.nombre}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.contacto || '—'}{r.telefono ? ` · ${r.telefono}` : ''}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.email || '—'}</td>
                <td className="py-2 px-3 text-center">{r.activo ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Dashboard del anunciante" onClick={() => setDashId(r.id)}><BarChart3 className="w-4 h-4 text-primary" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(r)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => void eliminar(r)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin anunciantes.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? 'Editar anunciante' : 'Nuevo anunciante'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Nombre / marca"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Contacto"><input className={fieldCls} value={edit.contacto ?? ''} onChange={(e) => setEdit({ ...edit, contacto: e.target.value })} /></Campo>
              <Campo label="Teléfono"><input className={fieldCls} value={edit.telefono ?? ''} onChange={(e) => setEdit({ ...edit, telefono: e.target.value })} /></Campo>
            </div>
            <Campo label="Email"><input className={fieldCls} type="email" value={edit.email ?? ''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></Campo>
            <Campo label="Cliente para facturar (Partner)">
              <select className={fieldCls} value={edit.partner_id ?? ''} onChange={(e) => setEdit({ ...edit, partner_id: e.target.value })}>
                <option value="">— Sin vincular (no se podrá facturar) —</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
              </select>
            </Campo>
            <Campo label="Marca del catálogo (para medir ventas de SUS SKUs en el dashboard)">
              <select className={fieldCls} value={edit.marca_id ?? ''} onChange={(e) => setEdit({ ...edit, marca_id: e.target.value })}>
                <option value="">— Sin marca —</option>
                {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
              </select>
            </Campo>
            <Switch checked={edit.activo ?? true} onChange={(v) => setEdit({ ...edit, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}

      {dashId && <DashboardAnunciante id={dashId} onClose={() => setDashId(null)} />}
    </div>
  );
}
